import { loadPdfjs } from './extract.ts';

/**
 * Vorschaubilder für die Übersicht (docs/KONZEPT.md, Abschnitt 19).
 *
 * Das Bild entsteht **einmal beim Aufnehmen** und liegt danach klein und verschlüsselt
 * beim Dokument. Der Grund ist die Offline-Regel: Text und Daten liegen immer auf dem
 * Gerät, die Dateien nur auf Abruf. Eine Übersicht, die ihre Kacheln aus den Dateien
 * baut, würde beim ersten Blick das halbe Archiv herunterladen.
 */

/** Breite der Kachel im Speicher. Mehr braucht niemand für ein Deckblatt. */
const BREITE = 320;
/** Zur Sicherheit: Was darüber liegt, gehört nicht in ein CRDT-Dokument. */
const MAX_ZEICHEN = 60_000;

function ausLeinwand(canvas: HTMLCanvasElement): string | null {
  const data = canvas.toDataURL('image/jpeg', 0.6);
  return data.length > MAX_ZEICHEN ? null : data;
}

async function ausBild(bytes: Uint8Array, mime: string): Promise<string | null> {
  const bitmap = await createImageBitmap(new Blob([bytes as BlobPart], { type: mime }));
  const faktor = Math.min(1, BREITE / bitmap.width);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * faktor));
  canvas.height = Math.max(1, Math.round(bitmap.height * faktor));
  const context = canvas.getContext('2d');
  if (!context) return null;
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return ausLeinwand(canvas);
}

async function ausPdf(bytes: Uint8Array): Promise<string | null> {
  const pdfjs = await loadPdfjs();
  // Kopie: pdfjs entkoppelt den übergebenen Speicher (siehe extract.ts).
  const pdf = await pdfjs.getDocument({ data: bytes.slice() }).promise;
  try {
    const seite = await pdf.getPage(1);
    const basis = seite.getViewport({ scale: 1 });
    const viewport = seite.getViewport({ scale: BREITE / basis.width });
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    const context = canvas.getContext('2d');
    if (!context) return null;
    // Weißer Grund: Ein PDF hat keinen, und ohne ihn steht schwarze Schrift auf Schwarz.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    await seite.render({ canvas, canvasContext: context, viewport } as never).promise;
    return ausLeinwand(canvas);
  } finally {
    await pdf.cleanup();
  }
}

/** Deckblatt einer Datei, oder `null` — dann zeigt die Kachel den Text. */
export async function renderThumb(
  bytes: Uint8Array,
  mime: string,
  name: string,
): Promise<string | null> {
  try {
    if (mime.startsWith('image/')) return await ausBild(bytes, mime);
    if (mime === 'application/pdf' || name.toLowerCase().endsWith('.pdf')) {
      return await ausPdf(bytes);
    }
  } catch (error) {
    console.warn('[archiv] Vorschau nicht möglich:', error);
  }
  return null;
}
